/**
 * Append a new `consent_logs` row to grant or revoke a category for
 * the current parent. Append-only — never UPDATE / DELETE the prior
 * row (UK-GDPR Art 7 audit trail).
 *
 * Wave 3.5 — used by the Parent Settings → Privacy toggles. The
 * parent ticks a category off → we append a new row with revoked_at
 * set to NOW(); the parent re-ticks → we append another with
 * revoked_at NULL. `useParentConsents` from Wave 3.4b reads "latest
 * row per type wins", so the toggle round-trips immediately.
 *
 * The consent_log RLS gate is `consent_insert` from migration 00004
 * which enforces `user_id = auth.uid()` — this is a self-only write,
 * no edge function needed.
 */

import { supabase } from '../supabase';
import {
  PARENTAL_CONSENT_DB_TYPE,
  type UkConsentCategory,
} from '@4cm/core/auth';

export interface ToggleConsentArgs {
  /** The user-facing category (mapped to DB enum via PARENTAL_CONSENT_DB_TYPE). */
  category: UkConsentCategory;
  /** True = grant (revoked_at null). False = revoke (revoked_at = now). */
  grant: boolean;
  /** Privacy notice version under which the change is recorded. */
  noticeVersion?: string;
}

export type ToggleConsentResult =
  | { ok: true; consentLogId: string }
  | { ok: false; error: string };

export async function toggleParentalConsent(
  args: ToggleConsentArgs,
): Promise<ToggleConsentResult> {
  const enumValue = PARENTAL_CONSENT_DB_TYPE[args.category];
  if (!enumValue) {
    return { ok: false, error: `unknown category: ${args.category}` };
  }

  // account_creation is required to use 4CM at all. Revoking it is
  // equivalent to closing the account — that path goes through the
  // erasure DSR flow (data-subject-delete), not a consent toggle.
  if (args.category === 'account_creation' && !args.grant) {
    return {
      ok: false,
      error: "Revoking 'account_creation' isn't a toggle — use 'Delete account' instead.",
    };
  }

  const { data: sessionRes } = await supabase.auth.getSession();
  const userId = sessionRes?.session?.user?.id;
  if (!userId) {
    return { ok: false, error: 'You need to be signed in to change consents.' };
  }

  const now = new Date().toISOString();
  const row = {
    user_id: userId,
    consent_type: enumValue,
    granted_at: now,
    revoked_at: args.grant ? null : now,
    version: `parental-toggle:notice=${args.noticeVersion ?? 'v1.0'}`,
  };

  const { data, error } = await supabase
    .from('consent_logs')
    .insert(row)
    .select('id')
    .maybeSingle();

  if (error) {
    return {
      ok: false,
      error: error.message ?? 'Could not save the change. Please try again.',
    };
  }
  return { ok: true, consentLogId: String(data?.id ?? '') };
}
